import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

interface User {
  id: string;
  email: string;
  role: "admin" | "employee";
  fullName?: string;
  designation?: string;
  department?: string;
  company?: string;
  employeeType?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getBackendUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) {
    console.error(
      "REACT_APP_BACKEND_URL is not set. API calls will hit the frontend origin."
    );
  }
  return base.endsWith("/") || base === "" ? base : `${base}/`;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!user;

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("email");
    delete axios.defaults.headers.common["Authorization"];
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem("authToken");
        const storedUser = localStorage.getItem("authUser");

        if (storedToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setToken(storedToken);
            setUser(parsedUser);
            axios.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${storedToken}`;
          } catch (error) {
            console.error("Error parsing stored user data:", error);
            localStorage.removeItem("authToken");
            localStorage.removeItem("authUser");
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    const normalizedEmail = email.trim().toLowerCase();
    const response = await axios.post(
      `${getBackendUrl()}api/auth/login`,
      {
        email: normalizedEmail,
        password,
      }
    );

    const { token: receivedToken, user: userData } = response.data;

    if (!receivedToken || !userData) {
      throw new Error("Invalid login response from server");
    }

    setToken(receivedToken);
    setUser(userData);

    localStorage.setItem("authToken", receivedToken);
    localStorage.setItem("authUser", JSON.stringify(userData));
    localStorage.setItem("email", normalizedEmail);

    axios.defaults.headers.common["Authorization"] = `Bearer ${receivedToken}`;

    return userData;
  };

  const logout = () => {
    clearAuth();
  };

  // 401 interceptor: only force logout for authenticated session failures,
  // never for login attempts (so the form can show the error message).
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestUrl = error.config?.url || "";
        const isLoginRequest = requestUrl.includes("/api/auth/login");

        if (error.response?.status === 401 && !isLoginRequest) {
          clearAuth();
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
